import { AxiosError } from "axios";
import {
  Button,
  Container,
  Form,
  Link,
  Message,
  RadioGroup,
  Stack,
  Text,
  useBasisForm,
} from "basis";
import { putHref } from "library/api/httpMethods";
import { formatMaskedValue } from "library/utils";
import { Dispatch } from "react";
import { useMutation } from "react-query";
import { CreditCard } from "../../CreditCard/CreditCard";
import {
  Action,
  ActionType,
  PersonalDetails,
  ScreenProps,
  StatementPreferenceType,
  UserAddress,
} from "../reducer/types";
import { StatementPreferencesResponse } from "../response/response";

const ViewFullAddress = ({ dispatch }: { dispatch: Dispatch<Action> }) => (
  <>
    <Link
      href="#"
      newTab={false}
      onClick={(e) => {
        e.preventDefault();
        dispatch({ type: ActionType.OTP });
      }}
    >
      View full address
    </Link>
    .
  </>
);

const formatAddress = (address: UserAddress): string | JSX.Element => {
  if (typeof address === "string") return formatMaskedValue(address);
  return (
    <Stack>
      <Text>{address.address1}</Text>
      <Text>{address.address2}</Text>
      <Text>{address.city}</Text>
      <Text>{address.state}</Text>
      <Text>{address.postCode}</Text>
      <Text>{address.country}</Text>
    </Stack>
  );
};

const OnlineStatementText = ({
  productName,
  email,
  dispatch,
  viewFullAddress,
  isNZ,
}: {
  productName: string;
  email: string;
  dispatch: Dispatch<Action>;
  viewFullAddress: boolean;
  isNZ: boolean;
}) => (
  <>
    <Text>
      Your {productName} statements are currently available online and a monthly
      notification is being sent to:
    </Text>
    <Text margin="8 0 0 0">
      {formatMaskedValue(email)}.<br />
      {viewFullAddress && <ViewFullAddress dispatch={dispatch} />}
    </Text>
    <Text margin="8 0 0 0">
      You can update your details on the{" "}
      <Link
        href={
          isNZ
            ? "https://gemfinance.zendesk.com/hc/en-nz?gemid2=gemf-web%3Ahome%3Ahome%3Ahelp-centre%3Anot-specified"
            : "https://latitudefs.zendesk.com/hc/en-au/articles/360007747097-Update-your-details"
        }
        newTab
      >
        {isNZ ? "Gem Finance website" : "Latitude website"}
      </Link>
      .
    </Text>
  </>
);

const PaperToOnlineStatementText = ({
  productName,
  email,
  dispatch,
  viewFullAddress,
  isNZ,
}: {
  productName: string;
  email: string;
  dispatch: Dispatch<Action>;
  viewFullAddress: boolean;
  isNZ: boolean;
}) => (
  <>
    <Text>
      Your {productName} statements will be made available online and a monthly
      notification will be sent to:
    </Text>
    <Text margin="8 0 0 0">
      {formatMaskedValue(email)}.<br />
      {viewFullAddress && <ViewFullAddress dispatch={dispatch} />}
    </Text>
    <Text margin="8 0 0 0">
      You can update your details on the{" "}
      <Link
        href={
          isNZ
            ? "https://gemfinance.zendesk.com/hc/en-nz?gemid2=gemf-web%3Ahome%3Ahome%3Ahelp-centre%3Anot-specified"
            : "https://latitudefs.zendesk.com/hc/en-au/articles/360007747097-Update-your-details"
        }
        newTab
      >
        {isNZ ? "Gem Finance website" : "Latitude website"}
      </Link>
      .
    </Text>
    <Text margin="8 0 0 0">
      You will no longer be mailed paper statements, so check online regularly
      to view your statements. You can change your delivery preference to paper
      statements anytime.
    </Text>
  </>
);

const PaperStatementText = ({
  productName,
  address,
  dispatch,
  viewFullAddress,
  initialOnline,
  isNZ,
}: {
  productName: string;
  address: UserAddress;
  dispatch: Dispatch<Action>;
  viewFullAddress: boolean;
  initialOnline: boolean;
  isNZ: boolean;
}) => (
  <>
    <Text>
      Your {productName} statements {initialOnline ? "will be " : "are being "}
      sent to:
    </Text>
    <Text margin="8 0 0 0">
      {formatAddress(address)}. <br />
      {viewFullAddress && <ViewFullAddress dispatch={dispatch} />}
    </Text>
    <Text margin="8 0 0 0">
      You can update your details on the{" "}
      <Link
        href={
          isNZ
            ? "https://gemfinance.zendesk.com/hc/en-nz?gemid2=gemf-web%3Ahome%3Ahome%3Ahelp-centre%3Anot-specified"
            : "https://latitudefs.zendesk.com/hc/en-au/articles/360007747097-Update-your-details"
        }
        newTab
      >
        {isNZ ? "Gem Finance website" : "Latitude website"}
      </Link>
      .
    </Text>
  </>
);

type FormValues = {
  preference: "ONLINE" | "PAPER";
};

export const StatementPreferenceForm = ({ state, dispatch }: ScreenProps) => {
  const {
    accountInfo,
    statementPreferences: statePref,
    personalDetails: details,
    submitUrl,
  } = state;
  const statementPreferences = statePref as StatementPreferenceType;
  const personalDetails = details as PersonalDetails;

  const viewFullAddress = typeof personalDetails.address === "string";
  const { Field, methods } = useBasisForm<FormValues>({
    defaultValues: { preference: statementPreferences.preference },
  });

  const { preference } = methods.watch();

  const statementPreferenceMutation = useMutation<
    StatementPreferencesResponse,
    AxiosError,
    FormValues
  >(
    async (values) => {
      if (!submitUrl) throw new Error("SubmitUrl is required");
      const res = await putHref<StatementPreferencesResponse, FormValues>(
        submitUrl,
        values,
      );

      return res.data;
    },
    {
      onSuccess({ pref }) {
        dispatch({
          type: ActionType.SUCCESS,
          statementPreferences: { ...statementPreferences, preference: pref },
        });
      },
      onError() {
        dispatch({ type: ActionType.SUBMIT_FAILED });
      },
    },
  );

  const onSubmit = (values: FormValues) =>
    statementPreferenceMutation.mutate(values);

  const displayFee = (statementPreferences.fee / 100).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });

  return (
    <Stack gap="8">
      <Container bg="grey.t05" padding="4">
        <CreditCard
          cardName={accountInfo.cardName}
          cardEndsWith={accountInfo.cardEndsWith}
        />
      </Container>
      {statementPreferences.feeWaived &&
        statementPreferences.preference !== "ONLINE" && (
          <Message severity="info-or-minor" bg="secondary.lightBlue.t25">
            Your paper statement fee is currently waived. This waiver expires if
            you switch to online statements. The fee will apply if you switch
            back in the future, unless you contact us and your circumstances are
            eligible for a waiver.
          </Message>
        )}
      <Form methods={methods} onSubmit={onSubmit}>
        <Stack gap="8" width="100%" width-sm="548" flatten>
          <Field
            label="How would you like to receive your statements?"
            name="preference"
            options={[
              { label: "Online (free)", value: "ONLINE" },
              {
                label:
                  statementPreferences.fee === 0
                    ? "Paper"
                    : `Paper (${displayFee})`,
                value: "PAPER",
              },
            ]}
            as={RadioGroup}
          />
          {preference === "PAPER" && (
            <PaperStatementText
              productName={accountInfo.cardName}
              address={personalDetails.address}
              isNZ={accountInfo.isNZ}
              dispatch={dispatch}
              viewFullAddress={viewFullAddress}
              initialOnline={statementPreferences.preference === "ONLINE"}
            />
          )}
          {preference === "ONLINE" &&
            statementPreferences.preference === "PAPER" && (
              <PaperToOnlineStatementText
                productName={accountInfo.cardName}
                isNZ={accountInfo.isNZ}
                email={personalDetails.email}
                dispatch={dispatch}
                viewFullAddress={viewFullAddress}
              />
            )}
          {preference === "ONLINE" &&
            statementPreferences.preference !== "PAPER" && (
              <OnlineStatementText
                productName={accountInfo.cardName}
                email={personalDetails.email}
                isNZ={accountInfo.isNZ}
                dispatch={dispatch}
                viewFullAddress={viewFullAddress}
              />
            )}
          <Button
            variant="primary"
            type="submit"
            loading={statementPreferenceMutation.isLoading}
          >
            Update
          </Button>
        </Stack>
      </Form>
    </Stack>
  );
};
